/**
 * Organization Ecosystem service (Phase 2.2).
 * Separate domain — integrates with Competition via optional organizationId FK.
 * Does not modify Tournament Engine / Player aggregates / TournamentRenderer.
 */

import { auth } from "../firebase/config";
import * as organizationRepository from "../repositories/organizationRepository";
import * as membershipRepository from "../repositories/organizationMembershipRepository";
import * as arenaRepository from "../repositories/arenaRepository";
import { ensureMyPlayer } from "./playerProfileService";
import { resolvePlayer } from "./identityService";
import { fetchUserProfile } from "../repositories/userRepository";
import { notify } from "./notificationService";
import { NOTIFICATION_KINDS, MAX_NOTIFICATION_FANOUT } from "../domain/notifications/notificationTypes";
import { updateCompetition } from "./competitionManagementService";
import {
  ORGANIZATION_ROLES,
  ORGANIZATION_TYPES,
  MEMBERSHIP_STATUSES,
  createOrganizationShell,
  createMembershipShell,
  buildOrganizationSearchKeywords,
  normalizeOrganizationName,
  filterOrganizations,
  projectOrganizationCard,
  canManageOrganization,
  newOrganizationId,
  newMembershipId,
} from "../domain/organization/organizationTypes";
import { collectOrganizationIdsForSport } from "../domain/sport/sportDiscoverScope";

function currentUid() {
  return auth.currentUser?.uid || null;
}

async function resolvePlayerId(uid) {
  try {
    const player = await ensureMyPlayer(uid);
    return player?.playerId || null;
  } catch {
    return null;
  }
}

function assertStaff(org, membership, uid) {
  if (!canManageOrganization(org, membership, uid)) {
    throw new Error("Only organization owners or administrators can do that.");
  }
}

/** Owner / admin / creator uids for targeted staff alerts (capped). */
function organizationStaffUserIds(org) {
  const ids = [
    org?.createdBy,
    ...(org?.ownerIds || []),
    ...(org?.adminUids || []),
  ]
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  return [...new Set(ids)].slice(0, MAX_NOTIFICATION_FANOUT);
}

/**
 * Attach public display fields for member / join-request rows (never show raw auth UIDs in UI).
 * @param {object[]} rows
 * @returns {Promise<object[]>}
 */
async function enrichMembershipDisplay(rows) {
  return Promise.all(
    (rows || []).map(async (m) => {
      let displayName = "";
      let username = null;
      let playerId = m.playerId || null;

      if (playerId) {
        try {
          const player = await resolvePlayer(playerId);
          if (player) {
            displayName = String(player.displayName || "").trim();
            username = player.username || null;
            playerId = player.playerId || playerId;
          }
        } catch {
          /* ignore */
        }
      }

      if (!displayName && m.userId) {
        try {
          const profile = await fetchUserProfile(m.userId);
          if (profile?.playerId && !playerId) {
            playerId = profile.playerId;
            const player = await resolvePlayer(profile.playerId);
            if (player) {
              displayName = String(player.displayName || "").trim();
              username = player.username || null;
            }
          }
          if (!displayName) {
            displayName = String(profile?.displayName || "").trim();
          }
        } catch {
          /* ignore */
        }
      }

      return {
        ...m,
        playerId,
        displayName: displayName || "Member",
        username,
      };
    })
  );
}

/**
 * Create organization + owner membership.
 * @param {object} input
 */
export async function createOrganization(input = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const name = String(input.name || "").trim();
  if (!name) throw new Error("Organization name is required.");
  const type = input.type || ORGANIZATION_TYPES.CLUB;
  if (!Object.values(ORGANIZATION_TYPES).includes(type)) {
    throw new Error("Invalid organization type.");
  }

  const organizationId = input.organizationId || newOrganizationId();
  const shell = createOrganizationShell({
    ...input,
    organizationId,
    name,
    type,
    createdBy: uid,
    ownerIds: [uid],
    adminUids: [],
    memberCount: 1,
  });

  await organizationRepository.createOrganization(organizationId, shell);

  const playerId = await resolvePlayerId(uid);
  const membershipId = newMembershipId();
  const membership = createMembershipShell({
    membershipId,
    organizationId,
    userId: uid,
    playerId,
    status: MEMBERSHIP_STATUSES.ACTIVE,
    role: ORGANIZATION_ROLES.OWNER,
    roleKeys: [ORGANIZATION_ROLES.OWNER],
  });
  await membershipRepository.createMembership(membershipId, membership);

  return { organization: { ...shell, id: organizationId }, membership };
}

/**
 * @param {string} organizationId
 * @param {object} patch
 */
export async function updateOrganizationProfile(organizationId, patch = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const membership = await membershipRepository.findMembershipForUser(organizationId, uid);
  assertStaff(org, membership, uid);

  const name = patch.name != null ? String(patch.name).trim() : org.name;
  if (!name) throw new Error("Organization name is required.");
  const type = patch.type != null ? patch.type : org.type || org.kind;
  const next = {
    name,
    normalizedName: normalizeOrganizationName(name),
    type,
    kind: type,
    logoUrl: patch.logoUrl !== undefined ? patch.logoUrl || null : org.logoUrl,
    description: patch.description !== undefined ? patch.description : org.description,
    website: patch.website !== undefined ? patch.website : org.website,
    country: patch.country !== undefined ? patch.country : org.country,
    state: patch.state !== undefined ? patch.state : org.state,
    city: patch.city !== undefined ? patch.city : org.city,
    address: patch.address !== undefined ? patch.address : org.address,
    email: patch.email !== undefined ? patch.email : org.email,
    phone: patch.phone !== undefined ? patch.phone : org.phone,
    foundedYear: patch.foundedYear !== undefined ? patch.foundedYear : org.foundedYear,
    searchKeywords: buildOrganizationSearchKeywords(name, {
      type,
      city: patch.city !== undefined ? patch.city : org.city,
      state: patch.state !== undefined ? patch.state : org.state,
      country: patch.country !== undefined ? patch.country : org.country,
    }),
    updatedAtMs: Date.now(),
  };
  await organizationRepository.updateOrganization(organizationId, next);
  return { ...org, ...next };
}

/**
 * Upload compressed organization / club theme photo via MediaService → R2.
 * Rolls back R2 if Firestore update fails.
 * @param {string} organizationId
 * @param {File} file
 * @param {{ onProgress?: (phase: string, detail?: object) => void }} [opts]
 */
export async function uploadOrganizationThemePhoto(organizationId, file, opts = {}) {
  const { onProgress } = opts;
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const membership = await membershipRepository.findMembershipForUser(organizationId, uid);
  assertStaff(org, membership, uid);

  const { uploadEntityThemePhoto, deleteEntityThemePhoto } = await import(
    "../repositories/cloudMediaStorage"
  );
  const { uploadEntityThemePhotoWithLocalFallback } = await import(
    "../repositories/localEntityThemePhotoStorage"
  );
  const { withMediaCacheBust, friendlyMediaError } = await import("./media/mediaUrl");
  const { isMediaApiConfigured } = await import("./media/mediaService");

  let logoUrl;
  let source;
  try {
    ({ url: logoUrl, source } = await uploadEntityThemePhotoWithLocalFallback({
      kind: "organization",
      entityId: organizationId,
      file,
      onProgress,
      uploadCloud: uploadEntityThemePhoto,
    }));
  } catch (e) {
    throw friendlyMediaError(e);
  }

  const logoUpdatedAt = Date.now();
  const storedUrl =
    source === "cloud" ? withMediaCacheBust(logoUrl, logoUpdatedAt) : logoUrl;
  onProgress?.("saving");
  try {
    await organizationRepository.updateOrganization(organizationId, {
      logoUrl: storedUrl,
      logoUpdatedAt,
      updatedAtMs: Date.now(),
    });
    onProgress?.("complete");
    return { ...org, logoUrl: storedUrl, logoUpdatedAt };
  } catch (e) {
    if (source === "cloud" && isMediaApiConfigured()) {
      try {
        await deleteEntityThemePhoto("organization", organizationId);
      } catch {
        /* best-effort rollback */
      }
    }
    throw friendlyMediaError(
      e instanceof Error
        ? Object.assign(e, { message: e.message || "Could not save logo" })
        : e
    );
  }
}

/**
 * @param {string} organizationId
 * @param {{ onProgress?: (phase: string) => void }} [opts]
 */
export async function removeOrganizationThemePhoto(organizationId, opts = {}) {
  const { onProgress } = opts;
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const membership = await membershipRepository.findMembershipForUser(organizationId, uid);
  assertStaff(org, membership, uid);
  const { deleteEntityThemePhoto } = await import("../repositories/cloudMediaStorage");
  const { removeLocalEntityThemePhoto } = await import(
    "../repositories/localEntityThemePhotoStorage"
  );
  const { friendlyMediaError } = await import("./media/mediaUrl");
  removeLocalEntityThemePhoto("organization", organizationId);
  onProgress?.("removing");
  try {
    await deleteEntityThemePhoto("organization", organizationId);
  } catch (e) {
    const status = /** @type {{ status?: number }} */ (e)?.status;
    if (status !== 404) throw friendlyMediaError(e);
  }
  onProgress?.("saving");
  await organizationRepository.updateOrganization(organizationId, {
    logoUrl: null,
    logoUpdatedAt: Date.now(),
    updatedAtMs: Date.now(),
  });
  onProgress?.("complete");
  return { ...org, logoUrl: null };
}

/**
 * @param {string} organizationId
 */
export async function getOrganization(organizationId) {
  return organizationRepository.fetchOrganization(organizationId);
}

/**
 * @param {{ query?: string, type?: string, country?: string, state?: string, sportId?: string, max?: number }} filters
 */
export async function searchOrganizations(filters = {}) {
  const q = (filters.query || "").trim();
  let rows =
    q.length >= 2
      ? await organizationRepository.searchOrganizationsByKeyword(q, { max: filters.max || 50 })
      : await organizationRepository.listOrganizations({ max: filters.max || 50 });

  let organizationIds = null;
  if (filters.sportId) {
    const arenas = await arenaRepository.listRecentArenas({ max: 80 });
    organizationIds = collectOrganizationIdsForSport(arenas, filters.sportId);
  }

  rows = filterOrganizations(rows, {
    ...filters,
    ...(organizationIds ? { organizationIds } : {}),
  });
  return rows.map(projectOrganizationCard);
}

/**
 * Staff invites a registered player into the organization.
 * Prefer `playerId` (player search). `userId` remains for tests / legacy callers.
 * Creates pending membership, or activates when `activateImmediately`.
 * @param {string} organizationId
 * @param {{
 *   playerId?: string,
 *   userId?: string,
 *   role?: string,
 *   activateImmediately?: boolean,
 *   message?: string,
 * }} input
 */
export async function inviteMember(organizationId, input = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");

  let targetUserId = String(input.userId || "").trim();
  let targetPlayerId = String(input.playerId || "").trim() || null;

  if (targetPlayerId) {
    const player = await resolvePlayer(targetPlayerId);
    if (!player) throw new Error("Player not found.");
    if (!player.userId) {
      throw new Error("That player has no linked account yet. They need to sign in first.");
    }
    targetUserId = String(player.userId).trim();
    targetPlayerId = player.playerId || targetPlayerId;
  }

  if (!targetUserId) throw new Error("Select a player to invite.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(organizationId, uid);
  assertStaff(org, staffMem, uid);

  if (targetUserId === uid) {
    throw new Error("You are already managing this organization.");
  }

  const existing = await membershipRepository.findMembershipForUser(organizationId, targetUserId);
  if (existing?.status === MEMBERSHIP_STATUSES.ACTIVE) {
    throw new Error("That player is already a member.");
  }

  const role = input.role || ORGANIZATION_ROLES.MEMBER;
  if (!Object.values(ORGANIZATION_ROLES).includes(role)) {
    throw new Error("Invalid role.");
  }
  if (role === ORGANIZATION_ROLES.OWNER && !(org.ownerIds || []).includes(uid) && org.createdBy !== uid) {
    throw new Error("Only an owner can invite another owner.");
  }

  const activate = Boolean(input.activateImmediately);
  const membershipId = existing?.membershipId || newMembershipId();
  const shell = createMembershipShell({
    membershipId,
    organizationId,
    userId: targetUserId,
    playerId: targetPlayerId || existing?.playerId || null,
    status: activate ? MEMBERSHIP_STATUSES.ACTIVE : MEMBERSHIP_STATUSES.PENDING,
    role,
    message: input.message || "Invited by administrator",
    decidedAtMs: activate ? Date.now() : null,
    decidedBy: activate ? uid : null,
  });

  if (existing) {
    await membershipRepository.updateMembership(membershipId, shell);
  } else {
    await membershipRepository.createMembership(membershipId, shell);
  }

  if (activate) {
    let ownerIds = [...(org.ownerIds || [])];
    let adminUids = [...(org.adminUids || [])];
    if (role === ORGANIZATION_ROLES.OWNER && !ownerIds.includes(targetUserId)) {
      ownerIds.push(targetUserId);
    }
    if (role === ORGANIZATION_ROLES.ADMIN && !adminUids.includes(targetUserId)) {
      adminUids.push(targetUserId);
    }
    await organizationRepository.updateOrganization(organizationId, {
      ownerIds,
      adminUids,
      memberCount: (org.memberCount || 0) + 1,
      updatedAtMs: Date.now(),
    });
  }

  void notify({
    kind: activate ? NOTIFICATION_KINDS.ORG_JOIN_APPROVED : NOTIFICATION_KINDS.ORG_INVITE,
    title: activate ? "Welcome to the organization" : "Organization invite",
    body: activate
      ? `You are now a member of ${org.name || "the organization"}`
      : `You've been invited to ${org.name || "an organization"}`,
    href: `/organization/${encodeURIComponent(organizationId)}`,
    organizationId,
    entityType: "organization",
    entityId: `invite:${membershipId}`,
    dedupeKey: `org_invite:${membershipId}`,
    userId: targetUserId,
  });

  return shell;
}

/**
 * Request to join an organization (pending until approved).
 * @param {string} organizationId
 * @param {{ message?: string }} [options]
 */
export async function requestJoinOrganization(organizationId, options = {}) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org || org.isActive === false) throw new Error("Organization not found.");

  const existing = await membershipRepository.findMembershipForUser(organizationId, uid);
  if (existing?.status === MEMBERSHIP_STATUSES.ACTIVE) {
    throw new Error("You are already a member.");
  }
  if (existing?.status === MEMBERSHIP_STATUSES.PENDING) {
    throw new Error("Join request already pending.");
  }

  const playerId = await resolvePlayerId(uid);
  const membershipId = existing?.membershipId || newMembershipId();
  const shell = createMembershipShell({
    membershipId,
    organizationId,
    userId: uid,
    playerId,
    status: MEMBERSHIP_STATUSES.PENDING,
    role: ORGANIZATION_ROLES.MEMBER,
    message: options.message || "",
  });

  if (existing) {
    await membershipRepository.updateMembership(membershipId, {
      ...shell,
      decidedAtMs: null,
      decidedBy: null,
    });
  } else {
    await membershipRepository.createMembership(membershipId, shell);
  }

  const staffIds = organizationStaffUserIds(org).filter((id) => id !== uid);
  if (staffIds.length) {
    const day = new Date().toISOString().slice(0, 10);
    void notify({
      kind: NOTIFICATION_KINDS.ORG_JOIN_REQUEST,
      title: "Join request",
      body: `Someone requested to join ${org.name || "your organization"}`,
      href: `/my-organization`,
      organizationId,
      entityType: "membership",
      entityId: membershipId,
      groupKey: `org_join:${organizationId}:${day}`,
      groupTitle: (count) =>
        count > 1 ? `${count} join requests` : "Join request",
      groupBody: (count) =>
        count > 1
          ? `${count} players requested to join ${org.name || "your organization"}`
          : `Someone requested to join ${org.name || "your organization"}`,
      userIds: staffIds,
    });
  }

  return shell;
}

/**
 * @param {string} membershipId
 */
export async function withdrawJoinRequest(membershipId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const m = await membershipRepository.fetchMembership(membershipId);
  if (!m) throw new Error("Request not found.");
  if (m.userId !== uid) throw new Error("Not your request.");
  if (m.status !== MEMBERSHIP_STATUSES.PENDING) {
    throw new Error("Only pending requests can be withdrawn.");
  }
  await membershipRepository.updateMembership(membershipId, {
    status: MEMBERSHIP_STATUSES.WITHDRAWN,
    updatedAtMs: Date.now(),
    decidedAtMs: Date.now(),
    decidedBy: uid,
  });
}

/**
 * @param {string} membershipId
 */
export async function approveJoinRequest(membershipId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const m = await membershipRepository.fetchMembership(membershipId);
  if (!m) throw new Error("Request not found.");
  if (m.status !== MEMBERSHIP_STATUSES.PENDING) {
    throw new Error("Request is not pending.");
  }
  const org = await organizationRepository.fetchOrganization(m.organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(m.organizationId, uid);
  assertStaff(org, staffMem, uid);

  await membershipRepository.updateMembership(membershipId, {
    status: MEMBERSHIP_STATUSES.ACTIVE,
    role: m.role || ORGANIZATION_ROLES.MEMBER,
    roleKeys: [m.role || ORGANIZATION_ROLES.MEMBER],
    updatedAtMs: Date.now(),
    decidedAtMs: Date.now(),
    decidedBy: uid,
  });
  await organizationRepository.updateOrganization(m.organizationId, {
    memberCount: (org.memberCount || 0) + 1,
    updatedAtMs: Date.now(),
  });

  if (m.userId) {
    void notify({
      kind: NOTIFICATION_KINDS.ORG_JOIN_APPROVED,
      title: "Join approved",
      body: `You're now a member of ${org.name || "the organization"}`,
      href: `/organization/${encodeURIComponent(m.organizationId)}`,
      organizationId: m.organizationId,
      entityType: "membership",
      entityId: `approved:${membershipId}`,
      dedupeKey: `org_join_approved:${membershipId}`,
      userId: m.userId,
    });
  }

  return { ...m, status: MEMBERSHIP_STATUSES.ACTIVE };
}

/**
 * @param {string} membershipId
 */
export async function rejectJoinRequest(membershipId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const m = await membershipRepository.fetchMembership(membershipId);
  if (!m) throw new Error("Request not found.");
  if (m.status !== MEMBERSHIP_STATUSES.PENDING) {
    throw new Error("Request is not pending.");
  }
  const org = await organizationRepository.fetchOrganization(m.organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(m.organizationId, uid);
  assertStaff(org, staffMem, uid);

  await membershipRepository.updateMembership(membershipId, {
    status: MEMBERSHIP_STATUSES.REJECTED,
    updatedAtMs: Date.now(),
    decidedAtMs: Date.now(),
    decidedBy: uid,
  });
}

/**
 * Leave organization (active member). Owners cannot leave if sole owner.
 * @param {string} organizationId
 */
export async function leaveOrganization(organizationId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const m = await membershipRepository.findMembershipForUser(organizationId, uid);
  if (!m || m.status !== MEMBERSHIP_STATUSES.ACTIVE) {
    throw new Error("You are not an active member.");
  }
  if (m.role === ORGANIZATION_ROLES.OWNER) {
    const owners = (org.ownerIds || []).filter((id) => id !== uid);
    if (owners.length === 0) {
      throw new Error("Transfer ownership before leaving as the sole owner.");
    }
  }

  await membershipRepository.updateMembership(m.membershipId, {
    status: MEMBERSHIP_STATUSES.ENDED,
    updatedAtMs: Date.now(),
  });

  const ownerIds = (org.ownerIds || []).filter((id) => id !== uid);
  const adminUids = (org.adminUids || []).filter((id) => id !== uid);
  await organizationRepository.updateOrganization(organizationId, {
    ownerIds,
    adminUids,
    memberCount: Math.max(0, (org.memberCount || 1) - 1),
    updatedAtMs: Date.now(),
  });
}

/**
 * @param {string} membershipId
 */
export async function removeMember(membershipId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const m = await membershipRepository.fetchMembership(membershipId);
  if (!m) throw new Error("Membership not found.");
  const org = await organizationRepository.fetchOrganization(m.organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(m.organizationId, uid);
  assertStaff(org, staffMem, uid);
  if (m.userId === uid) throw new Error("Use leave to remove yourself.");
  if (m.role === ORGANIZATION_ROLES.OWNER && (org.ownerIds || []).length <= 1) {
    throw new Error("Cannot remove the sole owner.");
  }

  await membershipRepository.updateMembership(membershipId, {
    status: MEMBERSHIP_STATUSES.ENDED,
    updatedAtMs: Date.now(),
    decidedAtMs: Date.now(),
    decidedBy: uid,
  });
  await organizationRepository.updateOrganization(m.organizationId, {
    ownerIds: (org.ownerIds || []).filter((id) => id !== m.userId),
    adminUids: (org.adminUids || []).filter((id) => id !== m.userId),
    memberCount: Math.max(0, (org.memberCount || 1) - 1),
    updatedAtMs: Date.now(),
  });
}

/**
 * @param {string} membershipId
 * @param {string} role
 */
export async function assignMemberRole(membershipId, role) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const allowed = Object.values(ORGANIZATION_ROLES);
  if (!allowed.includes(role)) throw new Error("Invalid role.");
  const m = await membershipRepository.fetchMembership(membershipId);
  if (!m || m.status !== MEMBERSHIP_STATUSES.ACTIVE) {
    throw new Error("Active membership required.");
  }
  const org = await organizationRepository.fetchOrganization(m.organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(m.organizationId, uid);
  assertStaff(org, staffMem, uid);

  // Only owners can promote to owner / demote owners
  const isOwner = (org.ownerIds || []).includes(uid) || org.createdBy === uid;
  if (
    (role === ORGANIZATION_ROLES.OWNER || m.role === ORGANIZATION_ROLES.OWNER) &&
    !isOwner
  ) {
    throw new Error("Only an owner can change owner roles.");
  }

  let ownerIds = [...(org.ownerIds || [])];
  let adminUids = [...(org.adminUids || [])];
  ownerIds = ownerIds.filter((id) => id !== m.userId);
  adminUids = adminUids.filter((id) => id !== m.userId);
  if (role === ORGANIZATION_ROLES.OWNER) ownerIds.push(m.userId);
  if (role === ORGANIZATION_ROLES.ADMIN) adminUids.push(m.userId);
  if (role === ORGANIZATION_ROLES.OWNER && ownerIds.length === 0) {
    throw new Error("Organization must keep at least one owner.");
  }

  await membershipRepository.updateMembership(membershipId, {
    role,
    roleKeys: [role],
    updatedAtMs: Date.now(),
  });
  await organizationRepository.updateOrganization(m.organizationId, {
    ownerIds,
    adminUids,
    updatedAtMs: Date.now(),
  });
}

/**
 * Memberships for signed-in user.
 */
export async function listMyMemberships() {
  const uid = currentUid();
  if (!uid) return [];
  const rows = await membershipRepository.listMembershipsForUser(uid);
  const orgs = await Promise.all(
    rows.map(async (m) => {
      const org = await organizationRepository.fetchOrganization(m.organizationId);
      return { membership: m, organization: org ? projectOrganizationCard(org) : null };
    })
  );
  return orgs.filter((x) => x.organization);
}

/**
 * Admin-capable orgs for /my-organization.
 * Uses `canManageOrganization` (createdBy · ownerIds · adminUids · active Owner/Admin membership).
 */
export async function listMyManagedOrganizations() {
  const uid = currentUid();
  if (!uid) return [];
  const rows = await membershipRepository.listMembershipsForUser(uid);
  const managed = [];
  for (const m of rows) {
    const org = await organizationRepository.fetchOrganization(m.organizationId);
    if (!org) continue;
    if (!canManageOrganization(org, m, uid)) continue;
    managed.push({ membership: m, organization: projectOrganizationCard(org) });
  }
  return managed;
}

/**
 * Home / organizer CTA gate — true when the user can manage ≥1 organization
 * (creator, owner, or administrator per Phase 2.2 RBAC).
 */
export async function currentUserCanHostTournament() {
  const managed = await listMyManagedOrganizations();
  return managed.length > 0;
}

/**
 * @param {string} organizationId
 */
export async function listOrganizationMembers(organizationId) {
  const rows = await membershipRepository.listMembershipsForOrganization(organizationId, {
    max: 120,
  });
  const active = rows.filter((r) => r.status === MEMBERSHIP_STATUSES.ACTIVE);
  return enrichMembershipDisplay(active);
}

/**
 * @param {string} organizationId
 */
export async function listPendingJoinRequests(organizationId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org) throw new Error("Organization not found.");
  const staffMem = await membershipRepository.findMembershipForUser(organizationId, uid);
  assertStaff(org, staffMem, uid);
  const rows = await membershipRepository.listMembershipsForOrganization(organizationId, {
    max: 80,
  });
  const pending = rows.filter((r) => r.status === MEMBERSHIP_STATUSES.PENDING);
  return enrichMembershipDisplay(pending);
}

/**
 * Competitions optionally linked to this organization.
 * @param {string} organizationId
 */
export async function listOrganizationCompetitions(organizationId) {
  if (!organizationId) return { upcoming: [], past: [] };
  let rows = [];
  try {
    rows = await arenaRepository.listCompetitionsByOrganization(organizationId, {
      max: 40,
    });
  } catch {
    const recent = await arenaRepository.listRecentCompetitions({ max: 60 });
    rows = recent.filter((c) => c.organizationId === organizationId);
  }
  const now = Date.now();
  const upcoming = [];
  const past = [];
  for (const c of rows) {
    const end = c.dates?.endAtMs ?? c.dates?.endMs ?? null;
    const status = c.status || "";
    const done =
      status === "completed" ||
      status === "archived" ||
      (end != null && end < now);
    const card = {
      competitionId: c.competitionId || c.id,
      name: c.name || "Tournament",
      status,
      startAtMs: c.dates?.startAtMs ?? null,
      endAtMs: end,
    };
    if (done) past.push(card);
    else upcoming.push(card);
  }
  return { upcoming, past };
}

/**
 * Public org page payload.
 * @param {string} organizationId
 * @param {string} [viewerUid]
 */
export async function getOrganizationPage(organizationId, viewerUid) {
  const org = await organizationRepository.fetchOrganization(organizationId);
  if (!org || org.isActive === false) return null;
  const members = await listOrganizationMembers(organizationId);
  const competitions = await listOrganizationCompetitions(organizationId);
  let myMembership = null;
  if (viewerUid) {
    myMembership = await membershipRepository.findMembershipForUser(organizationId, viewerUid);
  }
  const stats = {
    memberCount: org.memberCount ?? members.length,
    competitionCount: competitions.upcoming.length + competitions.past.length,
    upcomingCount: competitions.upcoming.length,
  };
  const achievements = [];
  if (stats.memberCount >= 10) {
    achievements.push({ id: "members_10", label: "Growing community (10+ members)" });
  }
  if (competitions.past.length >= 1) {
    achievements.push({ id: "hosted_1", label: "Hosted a competition" });
  }

  return {
    organization: org,
    card: projectOrganizationCard(org),
    members,
    competitions,
    stats,
    achievements,
    myMembership,
    canManage: canManageOrganization(org, myMembership, viewerUid),
  };
}

/**
 * Optional Competition ↔ Organization association (no tournament engine change).
 * @param {string} competitionId
 * @param {string | null} organizationId
 */
export async function associateCompetitionOrganization(competitionId, organizationId) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  if (organizationId) {
    const org = await organizationRepository.fetchOrganization(organizationId);
    if (!org) throw new Error("Organization not found.");
  }
  await updateCompetition(competitionId, {
    organizationId: organizationId || null,
    updatedAtMs: Date.now(),
  });
  return { competitionId, organizationId: organizationId || null };
}

/**
 * Active org badges for a user (player profile display).
 * @param {string} userId
 */
export async function listOrganizationBadgesForUser(userId) {
  if (!userId) return [];
  const rows = await membershipRepository.listMembershipsForUser(userId);
  const active = rows.filter((r) => r.status === MEMBERSHIP_STATUSES.ACTIVE);
  const badges = [];
  for (const m of active) {
    const org = await organizationRepository.fetchOrganization(m.organizationId);
    if (!org || org.isActive === false) continue;
    badges.push({
      organizationId: org.organizationId || org.id,
      name: org.name,
      type: org.type || org.kind,
      typeLabel: projectOrganizationCard(org).typeLabel,
      logoUrl: org.logoUrl || null,
      role: m.role,
    });
  }
  return badges;
}

/**
 * Badges for a player via memberships matching playerId or linked userId.
 * @param {string} playerId
 * @param {string} [userId]
 */
export async function listOrganizationBadgesForPlayer(playerId, userId) {
  if (userId) return listOrganizationBadgesForUser(userId);
  if (!playerId) return [];
  // Fallback: no indexed playerId query yet — empty unless userId known
  return [];
}

export {
  ORGANIZATION_TYPES,
  ORGANIZATION_ROLES,
  MEMBERSHIP_STATUSES,
  canManageOrganization,
  projectOrganizationCard,
};
